/*
  PDP re-render — a colour is a sibling product
  ---------------------------------------------
  Combined listings put each colour on its own product, so a colour swatch is a link
  (`[data-pf-option-link]`, blocks/_product-options.liquid) and following it is a full
  page load. This takes the click instead: the sibling's URL is fetched through the
  Section Rendering API for every section on the page that reads the product, each
  wrapper is swapped for the fresh render, and the address bar moves with pushState.

  Nothing here knows how a gallery or a buy panel works. A swapped section is announced
  the way the theme editor announces one — `shopify:section:unload` on the old wrapper,
  `shopify:section:load` on the new — which every driver on this template already
  handles, so they tear down and re-mount without a second code path.

  Markup contract:
    [data-pdp]                  the product section's root; carries data-pdp-title and
                                data-pdp-url for the document title and history
    [data-product-dependent]    any element in a section whose output reads `product`;
                                its `.shopify-section` wrapper is re-rendered, and it
                                carries aria-busy while the replacement is on its way
    [data-pf-option-link]       a swatch that lives on another product
    [data-pf-value-id]          the option value's id, on every swatch input and link
    [data-pdp-gallery] img      the slides whose first candidates are preloaded

  The click is rarely the start of the wait. A swatch under the pointer, a finger on it
  or focus on it fetches the sibling ahead of time, and the fresh gallery's first images
  are preloaded off that markup, so a click on a hovered swatch swaps in a frame with the
  photographs already in cache. One guess is in flight at a time and a click retires it,
  and a click retires the last click's request too, so a colour the shopper passed over
  or clicked past never shares bandwidth with the one they are waiting on;
  the first slide is decoded before the swap so the gallery never lands white. A render is
  fetched once per page: Shopify marks these responses no-store, so nothing else caches
  them, and a colour's stock can be no staler than the page itself — the bound an
  ordinary PDP already has.

  Anything that fails — a non-2xx, a section the sibling's template does not have, a
  response that will not parse — falls back to following the link, so the worst case is
  the page load this replaces.
*/
(function () {
  if (window.__pdpRender) return;
  window.__pdpRender = true;

  var PREFIX = 'shopify-section-';
  // Past this the fetch becomes the page load it was replacing: a request that never
  // settles would hold the columns dimmed and unclickable.
  var TIMEOUT = 8000;
  // Sweeping the pointer across the row must not render every colour it crosses.
  var HOVER_DELAY = 80;
  // Slides warmed per prefetch — the pair on screen at desktop width.
  var WARM_IMAGES = 2;
  // Shopify answers 400 to a `sections=` list longer than this.
  var MAX_SECTIONS = 5;
  // How long the swap waits for the first slide to decode before going ahead without it.
  var IMAGE_WAIT = 1000;

  var pending = null;
  var chosen = false;
  var cache = {};
  var guessInFlight = null;
  var clickInFlight = null;
  var warmed = {};
  var hoverTimer = null;

  function root() {
    return document.querySelector('[data-pdp]');
  }

  function wrapperOf(node) {
    return node.closest('.shopify-section');
  }

  /* Every section that opted in, in document order; the fresh markup carries the same
     markers, so the list is rebuilt per render rather than kept. */
  function sectionIds() {
    var marks = document.querySelectorAll('[data-product-dependent]');
    var ids = [];
    for (var i = 0; i < marks.length; i++) {
      var wrapper = wrapperOf(marks[i]);
      if (!wrapper || wrapper.id.indexOf(PREFIX) !== 0) continue;
      var id = wrapper.id.slice(PREFIX.length);
      if (ids.indexOf(id) < 0) ids.push(id);
    }
    return ids;
  }

  /* The clicked value in its own row and the current pick in every other, so the sibling
     opens on the same size. Null when no size is chosen yet, or any row lacks ids: the
     link alone opens the sibling on its colour and nothing else. */
  function optionValues(link) {
    var panel = link.closest('[data-product-form]');
    if (!panel) return null;

    var groups = panel.querySelectorAll('[data-pf-option]');
    var ids = [];
    for (var g = 0; g < groups.length; g++) {
      var pick = groups[g].contains(link) ? link : groups[g].querySelector('[data-pf-option-input]:checked');
      var id = pick && pick.getAttribute('data-pf-value-id');
      if (!id) return null;
      ids.push(id);
    }
    return ids.length ? ids : null;
  }

  /* Every root about to be replaced, not only the PDP's: the sticky bar mirrors the panel
     and would otherwise still add the old colour while the new one is on its way. */
  function setBusy(busy) {
    var marks = document.querySelectorAll('[data-product-dependent]');
    for (var i = 0; i < marks.length; i++) {
      if (busy) marks[i].setAttribute('aria-busy', 'true');
      else marks[i].removeAttribute('aria-busy');
    }
  }

  /* Parsed in full before anything is touched, so a section the sibling cannot render
     leaves the page whole and the caller falls back to navigating. */
  function parse(ids, sections) {
    var parser = new DOMParser();
    var fresh = {};
    for (var i = 0; i < ids.length; i++) {
      var html = sections[ids[i]];
      if (typeof html !== 'string') throw new Error('Section ' + ids[i] + ' did not render');
      var doc = parser.parseFromString(html, 'text/html');
      var next = doc.getElementById(PREFIX + ids[i]) || doc.querySelector('.shopify-section');
      if (!next) throw new Error('Section ' + ids[i] + ' has no wrapper');
      // A parser document has scripting off, so `<noscript>` content arrives as live
      // elements — the recommendations rail's no-JS stylesheet would hide it for good.
      var noscript = next.querySelectorAll('noscript');
      for (var n = 0; n < noscript.length; n++) noscript[n].remove();
      fresh[ids[i]] = next;
    }
    return fresh;
  }

  function announce(wrapper, name, id) {
    wrapper.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: { sectionId: id },
      })
    );
  }

  /* The values checked in every row but the clicked one, by row and name — a size, on a
     product with the usual two. Names, not ids: each product in a listing numbers its own
     values, so the sibling's markup carries different ids for the same size. */
  function chosenValues(link) {
    var panel = link.closest('[data-product-form]');
    if (!panel) return null;

    var groups = panel.querySelectorAll('[data-pf-option]');
    var values = {};
    for (var g = 0; g < groups.length; g++) {
      if (groups[g].contains(link)) continue;
      var checked = groups[g].querySelector('[data-pf-option-input]:checked');
      if (checked) values[groups[g].getAttribute('data-pf-option')] = checked.value;
    }
    return values;
  }

  /* The fresh panel opens on its colour and no size, whatever the request asked —
     `_product-options` never checks one. The sizes chosen here are checked in the new
     markup before its driver runs, so a colour switch keeps them. */
  function checkValues(wrapper, values) {
    if (!values) return;
    for (var row in values) {
      var inputs = wrapper.querySelectorAll('[data-pf-option="' + row + '"] [data-pf-option-input]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].value === values[row]) inputs[i].checked = true;
      }
    }
  }

  function swap(ids, fresh, values) {
    for (var i = 0; i < ids.length; i++) {
      var old = document.getElementById(PREFIX + ids[i]);
      if (!old) continue;
      var next = document.importNode(fresh[ids[i]], true);
      announce(old, 'shopify:section:unload', ids[i]);
      old.replaceWith(next);
      checkValues(next, values);
      announce(next, 'shopify:section:load', ids[i]);
    }
  }

  /* The product's title is the only part of the tab title that changes; the rest is the
     layout's, so it is replaced in place rather than rebuilt. */
  function syncTitle(before, after) {
    var was = before && before.getAttribute('data-pdp-title');
    var now = after && after.getAttribute('data-pdp-title');
    if (!was || !now || document.title.indexOf(was) < 0) return;
    document.title = document.title.replace(was, now);
  }

  /* Mirrors syncUrl in assets/product-form.js: `variant` rides along once the customer
     has chosen — a size picked here, or a URL that already carried one — so back and
     forward land on the size they had. A customer who never chose is not pinned: the
     bare URL opens on the colour alone, as every landing does. */
  function syncHistory(target, pdp) {
    var next = new URL(target.href);
    var panel = pdp.querySelector('[data-product-form]');
    // What the fresh panel resolved on its first pass, not the native select or the link's
    // own query: both can name a variant nobody chose while the size is still unanswered.
    var variant = panel && panel.__pfVariant;
    var state = { pdp: true };

    next.searchParams.delete('variant');
    if ((chosen || new URL(window.location.href).searchParams.has('variant')) && variant) {
      next.searchParams.set('variant', variant.id);
    }
    if (next.href === window.location.href) return;
    window.history.pushState(state, '', next.href);
  }

  /* The swatch that was clicked is gone with its section; its counterpart in the fresh
     panel is the checked value of the same row, and focus lands there. */
  function restoreFocus(pdp, rowIndex) {
    if (rowIndex === null) return;
    var input = pdp.querySelector('[data-pf-option="' + rowIndex + '"] [data-pf-option-input]:checked');
    if (input) input.focus({ preventScroll: true });
  }

  /* ---------- Fetching ---------- */

  /* The cache key names every section; the wire carries at most MAX_SECTIONS per request,
     so a long list goes out as several requests in parallel and is merged on return. */
  function batches(href) {
    var url = new URL(href);
    var ids = (url.searchParams.get('sections') || '').split(',');
    var urls = [];
    for (var i = 0; i < ids.length; i += MAX_SECTIONS) {
      var part = new URL(href);
      part.searchParams.set('sections', ids.slice(i, i + MAX_SECTIONS).join(','));
      urls.push(part.href);
    }
    return urls;
  }

  /* One selection per request: with both, Shopify follows `option_values` and yet answers
     the sizes off `variant` — Olive asked for L came back on XS. Liquid checks no size
     either way; a size in the address is re-checked by the panel's driver on load. */
  function requestFor(target, optionValues) {
    var request = new URL(target.href);
    request.searchParams.set('sections', sectionIds().join(','));
    // A link's own `?variant=` is never sent: a swatch link is the sibling's bare path, and
    // a popstate target carries only what syncHistory wrote.
    request.searchParams.delete('variant');
    if (optionValues) request.searchParams.set('option_values', optionValues.join(','));
    return request.href;
  }

  /* The fresh gallery's first slides, preloaded off the parsed markup with the `<img>`'s
     own srcset and sizes so the browser fetches the candidate it will pick anyway. The
     links stay: a second preload of the same candidate is a cache hit. */
  function warmImages(sections) {
    var pdp = root();
    var wrapper = pdp && wrapperOf(pdp);
    var html = wrapper && sections[wrapper.id.slice(PREFIX.length)];
    if (typeof html !== 'string') return;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var images = doc.querySelectorAll('[data-pdp-gallery] img[srcset]');
    for (var i = 0; i < images.length && i < WARM_IMAGES; i++) {
      var key = images[i].getAttribute('srcset');
      if (warmed[key]) continue;
      warmed[key] = true;

      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.setAttribute('imagesrcset', key);
      link.setAttribute('imagesizes', images[i].getAttribute('sizes') || '');
      document.head.appendChild(link);
    }
  }

  /* The click owns one request. Whatever else was in flight for a click is retired: a
     colour clicked past is bandwidth taken from the one now waited on. */
  function claim(entry) {
    if (entry.settled) return;
    if (guessInFlight === entry) guessInFlight = null;
    if (clickInFlight && clickInFlight !== entry) clickInFlight.controller.abort();
    clickInFlight = entry;
  }

  function release(entry) {
    entry.settled = true;
    if (guessInFlight === entry) guessInFlight = null;
    if (clickInFlight === entry) clickInFlight = null;
  }

  /* One render per URL for the life of the page; a failed one is dropped so the next
     click retries. A guess is low priority and there is only ever one in flight: a newer
     guess or a click for another colour retires it, a click for the same colour adopts it.
     No Accept header: `application/json` on a product URL is Shopify's product endpoint. */
  function sectionsFor(href, guess) {
    var entry = cache[href];
    if (entry) {
      if (!guess) claim(entry);
      return entry.promise;
    }
    if (guessInFlight) guessInFlight.controller.abort();

    var controller = new AbortController();
    entry = { controller: controller, settled: false };
    entry.promise = Promise.all(
      batches(href).map(function (url) {
        return fetch(url, { signal: controller.signal, priority: guess ? 'low' : 'high' }).then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        });
      })
    )
      .then(function (parts) {
        var sections = {};
        for (var i = 0; i < parts.length; i++) {
          for (var id in parts[i]) sections[id] = parts[i][id];
        }
        release(entry);
        warmImages(sections);
        return sections;
      })
      .catch(function (error) {
        release(entry);
        if (cache[href] === entry) delete cache[href];
        throw error;
      });

    cache[href] = entry;
    if (guess) guessInFlight = entry;
    else claim(entry);
    return entry.promise;
  }

  function prefetch(link) {
    if (!root() || !window.fetch || !window.DOMParser || !window.AbortController) return;
    sectionsFor(requestFor(link, optionValues(link)), true).catch(function () {
      /* Speculative — the click will report the failure if it comes to that. */
    });
  }

  /* Resolves once the fresh gallery's first slide has decoded, or after IMAGE_WAIT —
     whichever is first. A cached image resolves at once, so a prefetched swap loses
     nothing; a cold one shows the old columns under the dim rather than a white gallery. */
  function firstSlideReady(fresh) {
    var img = null;
    for (var id in fresh) {
      img = fresh[id].querySelector('[data-pdp-gallery] img[srcset]');
      if (img) break;
    }
    if (!img || !window.Image || !('decode' in new Image())) return Promise.resolve();

    var probe = new Image();
    probe.sizes = img.getAttribute('sizes') || '';
    probe.srcset = img.getAttribute('srcset');
    probe.src = img.getAttribute('src') || '';

    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, IMAGE_WAIT);
      probe.decode().then(
        function () {
          clearTimeout(timer);
          resolve();
        },
        function () {
          clearTimeout(timer);
          resolve();
        }
      );
    });
  }

  function render(target, options) {
    var pdp = root();
    if (!pdp || !sectionIds().length) return false;

    var href = requestFor(target, options.optionValues);
    // A later click or a timeout retires this one; the token is what tells them apart.
    var token = {};
    pending = token;
    setBusy(true);

    function settle() {
      if (pending !== token) return;
      pending = null;
      setBusy(false);
    }

    function fail(reason) {
      console.warn('PDP: ' + reason + ' Following the link instead.');
      settle();
      window.location.assign(target.href);
    }

    var timer = setTimeout(function () {
      if (pending !== token) return;
      delete cache[href];
      fail('timed out.');
    }, TIMEOUT);

    sectionsFor(href, false)
      .then(function (sections) {
        clearTimeout(timer);
        if (pending !== token) return;
        var fresh = parse(sectionIds(), sections);
        return firstSlideReady(fresh).then(function () {
          if (pending !== token) return;

          // Tearing the old gallery down stacks its slides for a frame and scroll anchoring
          // follows them down the page; the reader has not moved, so the offset is kept.
          var offset = window.pageYOffset;
          swap(sectionIds(), fresh, options.values);
          window.scrollTo(0, offset);

          var next = root();
          syncTitle(pdp, next);
          if (options.push !== false) syncHistory(target, next);
          restoreFocus(next, options.rowIndex);
          settle();
        });
      })
      .catch(function (error) {
        clearTimeout(timer);
        if (pending !== token) return;
        fail(error.message);
      });

    return true;
  }

  /* ---------- Wiring ---------- */

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var link = target.closest('[data-pf-option-link]');
    if (!link || !link.closest('[data-pdp]')) return;
    // A modified click, or one something else already took, is the browser's.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // The editor navigates its own preview; a pushState here would leave it on the wrong product.
    if (window.Shopify && window.Shopify.designMode) return;
    if (!window.fetch || !window.DOMParser || !window.AbortController || !window.history.pushState) return;

    var row = link.closest('[data-pf-option]');
    var started = render(link, {
      optionValues: optionValues(link),
      values: chosenValues(link),
      rowIndex: row ? row.getAttribute('data-pf-option') : null,
    });
    if (started) e.preventDefault();
  });

  /* Hover intent on desktop, the touch itself on a phone, focus for the keyboard — each
     runs ahead of the click it usually precedes. `pointerenter` does not bubble, so the
     capture phase is what reaches the swatch. */
  function speculative(e) {
    var link = e.target && e.target.closest && e.target.closest('[data-pf-option-link]');
    if (!link || !link.closest('[data-pdp]')) return;
    if (window.Shopify && window.Shopify.designMode) return;

    clearTimeout(hoverTimer);
    if (e.type === 'pointerenter' && e.pointerType === 'mouse') {
      hoverTimer = setTimeout(function () {
        prefetch(link);
      }, HOVER_DELAY);
      return;
    }
    prefetch(link);
  }

  // A size picked on this page. The parent listing resolves no variant of its own, so
  // its URL never says the customer chose; this does.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest('[data-pf-option]') && t.closest('[data-pdp]')) chosen = true;
  });

  document.addEventListener('pointerenter', speculative, true);
  document.addEventListener('pointerdown', speculative, true);
  document.addEventListener('focusin', speculative);
  document.addEventListener(
    'pointerleave',
    function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-pf-option-link]')) clearTimeout(hoverTimer);
    },
    true
  );

  /* Back and forward between colours re-render rather than reload. Only entries this
     driver stamped, and only when the product actually differs — a hash entry on the same
     product is left alone. */
  window.addEventListener('popstate', function (e) {
    if (!e.state || !e.state.pdp) return;
    var pdp = root();
    if (!pdp) return;

    var current = new URL(pdp.getAttribute('data-pdp-url') || '/', window.location.href);
    if (current.pathname === window.location.pathname) return;
    render(new URL(window.location.href), { push: false, rowIndex: null });
  });

  /* The entry the page opened on has no state of its own; stamped so a return to it from
     a sibling re-renders instead of being ignored. */
  function stamp() {
    if (!root() || !window.history.replaceState) return;
    var state = window.history.state || {};
    if (state.pdp) return;
    state.pdp = true;
    window.history.replaceState(state, '', window.location.href);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stamp);
  } else {
    stamp();
  }

  /* Restored from the back-forward cache mid-fetch: the request died with the navigation
     but the busy state came back with the page, and nothing else would ever clear it. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    pending = null;
    setBusy(false);
  });
})();
